import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, FileText, X, CheckCircle, Image as ImageIcon, Files, CurrencyEur } from "@phosphor-icons/react";
import { getToken } from "@/lib/authToken";
const eur = (n) => (Number(n) || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const MAX_CHARS = 700;

/**
 * Bottom-sheet del modo móvil para emitir el Informe Preliminar.
 *
 * Flujo:
 *   1. Cargar resumen (notas actuales, reserva, # fotos, # docs).
 *   2. Pulsar "Desarrollar con IA" → endpoint `/mobile/develop-field-notes`.
 *      Devuelve un párrafo técnico/coloquial de máx. 700 chars.
 *   3. El perito puede editar/ajustar el texto antes de emitir.
 *   4. Pulsar "Emitir preliminar" → guarda las notas finales y llama al
 *      endpoint estándar `/informe-preliminar/generate` (PDF).
 *      El PDF se descarga abriendo el blob en pestaña nueva.
 */
export default function MobilePreliminarSheet({ expediente, onClose, onGenerated }) {
  const exp = expediente;
  const [notas, setNotas] = useState(exp.observaciones_especiales || "");
  const [developed, setDeveloped] = useState(false); // marca que ya fue desarrollado por IA
  const [aiLoading, setAiLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Contadores
  const [fotosCount, setFotosCount] = useState(null);
  const [docsCount, setDocsCount] = useState(null);

  const reserva = exp.reserva_preventiva_garantias || [];
  const totalReserva = reserva.reduce((acc, r) => acc + (Number(r.importe) || 0), 0);

  useEffect(() => {
    Promise.all([
      api.get(`/expedientes/${exp.id}/attachments`, { params: { kind: "photo" } }),
      api.get(`/expedientes/${exp.id}/attachments`, { params: { kind: "docs" } }),
    ])
      .then(([f, d]) => {
        setFotosCount((f.data || []).length);
        // Filtramos para no contar el "parte" original ni informes generados anteriormente.
        const newDocs = (d.data || []).filter((a) => a.kind !== "parte" && a.kind !== "informe");
        setDocsCount(newDocs.length);
      })
      .catch(() => {
        setFotosCount(0);
        setDocsCount(0);
      });
  }, [exp.id]);

  const developAi = async () => {
    if (!notas.trim()) {
      toast.error("Dicta primero algunas notas de campo");
      return;
    }
    setAiLoading(true);
    try {
      const r = await api.post(`/expedientes/${exp.id}/mobile/develop-field-notes`, { notas });
      setNotas(r.data.propuesta || notas);
      setDeveloped(true);
      toast.success(`Notas desarrolladas (${r.data.chars} caracteres)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "La IA no pudo desarrollar las notas");
    } finally {
      setAiLoading(false);
    }
  };

  const generate = async () => {
    if (!notas.trim()) {
      toast.error("Necesitas notas de campo");
      return;
    }
    if (notas.length > MAX_CHARS) {
      toast.error(`Las notas superan los ${MAX_CHARS} caracteres`);
      return;
    }
    setGenerating(true);
    try {
      // 1) Persistimos las notas finales (sin auto-gestión: ya están en el timeline)
      await api.put(`/expedientes/${exp.id}`, { observaciones_especiales: notas });
      // 2) Generamos PDF preliminar
      const gen = await api.post(`/expedientes/${exp.id}/informe-preliminar/generate`, { format: "pdf" });
      toast.success("Informe preliminar emitido");
      // 3) Abrir el PDF generado
      const token = getToken();
      const newWin = window.open("", "_blank");
      try {
        const res = await api.get(
          `/expedientes/${exp.id}/attachments/${gen.data.attachment_id}/file`,
          { params: { token }, responseType: "blob" },
        );
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        if (newWin && !newWin.closed) newWin.location.href = url;
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch {
        if (newWin && !newWin.closed) newWin.close();
        toast.message("PDF generado, ábrelo desde la pestaña Documentos");
      }
      onGenerated?.(gen.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo emitir el preliminar");
    } finally {
      setGenerating(false);
    }
  };

  const charsLeft = MAX_CHARS - notas.length;
  const charsTone =
    charsLeft < 0 ? "text-red-600" : charsLeft < 100 ? "text-amber-600" : "text-muted";

  return (
    <div className="fixed inset-0 z-30 flex items-end" data-testid="preliminar-sheet">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full bg-white rounded-t-xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-2 px-3 py-3 border-b border-[#E8EAED]">
          <FileText className="w-5 h-5 text-blue-600" weight="duotone" />
          <h3 className="font-semibold text-[15px] flex-1">Emitir informe preliminar</h3>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-zinc-100"
            aria-label="Cerrar"
            data-testid="preliminar-close"
          >
            <X className="w-5 h-5" weight="bold" />
          </button>
        </div>

        {/* Resumen */}
        <div className="px-3 py-3 border-b border-[#E8EAED] grid grid-cols-3 gap-2">
          <SummaryTile
            icon={<CurrencyEur className="w-4 h-4 text-emerald-600" weight="bold" />}
            label="Reserva"
            value={reserva.length > 0 ? `${eur(totalReserva)} · ${reserva.length} part.` : "Sin reserva"}
            warn={reserva.length === 0}
            testid="prel-summary-reserva"
          />
          <SummaryTile
            icon={<ImageIcon className="w-4 h-4 text-pink-600" weight="bold" />}
            label="Fotos"
            value={fotosCount == null ? "…" : String(fotosCount)}
            warn={fotosCount === 0}
            testid="prel-summary-fotos"
          />
          <SummaryTile
            icon={<Files className="w-4 h-4 text-blue-600" weight="bold" />}
            label="Documentos"
            value={docsCount == null ? "…" : String(docsCount)}
            testid="prel-summary-docs"
          />
        </div>

        {/* Notas */}
        <div className="overflow-y-auto p-3 flex-1">
          <div className="flex items-baseline justify-between mb-1">
            <label className="text-[11px] uppercase tracking-wide font-semibold text-muted">
              Notas de campo desarrolladas
            </label>
            <span className={`text-[11px] tabular-nums ${charsTone}`} data-testid="prel-notas-counter">
              {notas.length}/{MAX_CHARS}
            </span>
          </div>
          <textarea
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            rows={9}
            placeholder="Pulsa &quot;Desarrollar con IA&quot; para ampliar las notas dictadas, o edita manualmente."
            className="w-full text-[14px] leading-relaxed p-3 border border-[#E8EAED] rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
            data-testid="prel-notas-textarea"
          />
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={developAi}
              disabled={aiLoading || generating}
              className="flex-1 inline-flex items-center justify-center gap-1.5 bg-white border border-[#E8EAED] py-2.5 rounded-md text-[13px] font-semibold hover:bg-zinc-50 disabled:opacity-50"
              data-testid="prel-develop-ai"
            >
              <Sparkle className="w-4 h-4 text-violet-600" weight="fill" />
              {aiLoading ? "Desarrollando…" : developed ? "Desarrollar de nuevo" : "Desarrollar con IA"}
            </button>
          </div>
          <p className="text-[11px] text-muted mt-2 leading-snug">
            La IA ampliará tus notas hasta un máximo de {MAX_CHARS} caracteres, con redacción
            técnica pero lenguaje directo. Lo que aparece aquí será lo que se incluya en el PDF
            preliminar como descripción del siniestro.
          </p>
        </div>

        {/* Footer */}
        <div className="border-t border-[#E8EAED] px-3 py-3 flex gap-2">
          <button
            onClick={onClose}
            className="px-3 py-3 rounded-md border border-[#E8EAED] bg-white text-[13px] font-medium"
            data-testid="prel-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={generate}
            disabled={generating || aiLoading || !notas.trim() || notas.length > MAX_CHARS}
            className="flex-1 inline-flex items-center justify-center gap-1.5 bg-blue-600 text-white text-[14px] font-semibold py-3 rounded-md disabled:opacity-50 hover:bg-blue-700"
            data-testid="prel-generate"
          >
            <CheckCircle className="w-4 h-4" weight="fill" />
            {generating ? "Generando…" : "Emitir preliminar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryTile({ icon, label, value, warn = false, testid }) {
  return (
    <div
      className={`rounded-md border px-2 py-2 ${warn ? "border-amber-200 bg-amber-50/40" : "border-[#E8EAED] bg-white"}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1">
        {icon}
        <span className="text-[10px] uppercase tracking-wide font-semibold text-muted">{label}</span>
      </div>
      <div className={`text-[12px] font-semibold mt-0.5 truncate ${warn ? "text-amber-700" : "text-foreground"}`}>{value}</div>
    </div>
  );
}
